import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
  MenuItem,
} from "@mui/material";
import useUpdate from "../../api/useUpdate";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import {
  getFormDirection,
  getRtlSelectProps,
  getRtlTextFieldProps,
} from "../utils/rtlForm";

const toList = (data) => (Array.isArray(data) ? data : data?.results || []);
const belongsToShop = (item, shopId) =>
  !shopId || String(item?.shop ?? item?.shop_details?.id ?? "") === String(shopId);

const StockEdit = ({ open, onClose, editData, token }) => {
  const { t, i18n } = useTranslation();
  const formDirection = getFormDirection(i18n);
  const { fieldDirection } = formDirection;
  const selectProps = getRtlSelectProps(formDirection);
  const { handleUpdate, loading, isSuccess } = useUpdate("shop/stocks", token);

  const { data: shopsData, loading: shopLoading } = useFetchObjects(
    ["shop", "stock", "edit"],
    "shop/shop/?page_size=all",
    token
  );
  const { data: productsData, loading: productLoading } = useFetchObjects(
    ["products", "stock", "edit"],
    "shop/products/?page_size=all",
    token
  );
  const { data: suppliersData, loading: supplierLoading } = useFetchObjects(
    ["suppliers", "stock", "edit"],
    "shop/suppliers/?page_size=all",
    token
  );
  const { data: unitsData, loading: unitLoading } = useFetchObjects(
    ["units", "stock", "edit"],
    "shop/units/?page_size=all",
    token
  );
  const { data: currenciesData, loading: currencyLoading } = useFetchObjects(
    ["currencies", "stock", "edit"],
    "shop/currencies/?page_size=all",
    token
  );

  const shops = useMemo(() => toList(shopsData), [shopsData]);
  const products = useMemo(() => toList(productsData), [productsData]);
  const suppliers = useMemo(() => toList(suppliersData), [suppliersData]);
  const units = useMemo(() => toList(unitsData), [unitsData]);
  const currencies = useMemo(() => toList(currenciesData), [currenciesData]);

  const [selectedShop, setSelectedShop] = useState("");
  const [product, setProduct] = useState("");
  const [supplier, setSupplier] = useState("");
  const [buyPrice, setBuyPrice] = useState("");
  const [wholesalePrice, setWholesalePrice] = useState("");
  const [retailPrice, setRetailPrice] = useState("");
  const [quantity, setQuantity] = useState("");
  const [unit, setUnit] = useState("");
  const [currency, setCurrency] = useState("");

  const branchProducts = useMemo(
    () => products.filter((item) => belongsToShop(item, selectedShop)),
    [products, selectedShop]
  );
  const branchSuppliers = useMemo(
    () => suppliers.filter((item) => belongsToShop(item, selectedShop)),
    [suppliers, selectedShop]
  );
  const branchUnits = useMemo(
    () => units.filter((item) => belongsToShop(item, selectedShop)),
    [units, selectedShop]
  );
  const branchCurrencies = useMemo(
    () => currencies.filter((item) => belongsToShop(item, selectedShop)),
    [currencies, selectedShop]
  );

  useEffect(() => {
    if (editData) {
      setSelectedShop(String(editData.shop || editData.shop_details?.id || ""));
      setProduct(String(editData.product || editData.product_details?.id || editData.purchase_item_details?.product?.id || ""));
      setSupplier(String(editData.supplier || editData.supplier_details?.id || editData.purchase_item_details?.purchase?.supplier?.id || ""));
      setBuyPrice(String(editData.buy_price || editData.purchase_item_details?.buy_price || ""));
      setWholesalePrice(String(editData.wholesale_price || editData.purchase_item_details?.wholesale_price || ""));
      setRetailPrice(String(editData.retail_price || editData.purchase_item_details?.retail_price || editData.sale_price || ""));
      setQuantity(String(editData.quantity || ""));
      setUnit(String(editData.unit || editData.unit_details?.id || ""));
      setCurrency(String(editData.currency || editData.currency_details?.id || ""));
    }
  }, [editData]);

  const handleShopChange = (shopId) => {
    setSelectedShop(shopId);
    setProduct("");
    setSupplier("");
    setUnit("");
    setCurrency("");
  };

  const handleSubmit = () => {
    const payload = {
      shop: selectedShop,
      product: product || null,
      supplier: supplier || null,
      buy_price: buyPrice === "" ? null : buyPrice,
      wholesale_price: wholesalePrice === "" ? null : wholesalePrice,
      retail_price: retailPrice === "" ? null : retailPrice,
      sale_price: retailPrice === "" ? "0" : retailPrice,
      quantity: quantity === "" ? 0 : quantity,
      unit: unit || null,
      currency,
    };
    handleUpdate(editData.id, payload);
  };

  useEffect(() => {
    if (isSuccess) {
      setSelectedShop("");
      setProduct("");
      setSupplier("");
      setBuyPrice("");
      setWholesalePrice("");
      setRetailPrice("");
      setQuantity("");
      setUnit("");
      setCurrency("");
      onClose();
    }
  }, [isSuccess, onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" dir={fieldDirection}>
      <DialogTitle>{t("stock.editStock")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1, direction: fieldDirection }}>
          <TextField
            select
            margin="dense"
            label={t("stock.selectShop")}
            fullWidth
            required
            value={selectedShop}
            onChange={(e) => handleShopChange(e.target.value)}
            disabled={shopLoading}
            SelectProps={selectProps}
            {...getRtlTextFieldProps(formDirection)}
          >
            {shops.map((shop) => (
              <MenuItem key={shop.id} value={String(shop.id)}>
                {shop.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            margin="dense"
            label={t("stock.selectProduct")}
            fullWidth
            value={product}
            onChange={(e) => setProduct(e.target.value)}
            disabled={productLoading || !selectedShop}
            SelectProps={selectProps}
            {...getRtlTextFieldProps(formDirection)}
          >
            {branchProducts.map((item) => (
              <MenuItem key={item.id} value={String(item.id)}>
                {item.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            margin="dense"
            label={t("stock.selectSupplier")}
            fullWidth
            value={supplier}
            onChange={(e) => setSupplier(e.target.value)}
            disabled={supplierLoading || !selectedShop}
            SelectProps={selectProps}
            {...getRtlTextFieldProps(formDirection)}
          >
            <MenuItem value="">{t("stock.noSupplier")}</MenuItem>
            {branchSuppliers.map((item) => (
              <MenuItem key={item.id} value={String(item.id)}>
                {item.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            margin="dense"
            label={t("stock.buyPrice")}
            type="number"
            fullWidth
            value={buyPrice}
            onChange={(e) => setBuyPrice(e.target.value)}
            {...getRtlTextFieldProps(formDirection)}
          />
          <TextField
            margin="dense"
            label={t("stock.wholesalePrice")}
            type="number"
            fullWidth
            value={wholesalePrice}
            onChange={(e) => setWholesalePrice(e.target.value)}
            {...getRtlTextFieldProps(formDirection)}
          />
          <TextField
            margin="dense"
            label={t("stock.retailPrice")}
            type="number"
            fullWidth
            value={retailPrice}
            onChange={(e) => setRetailPrice(e.target.value)}
            {...getRtlTextFieldProps(formDirection)}
          />
          <TextField
            margin="dense"
            label={t("stock.availableQty")}
            type="number"
            fullWidth
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            {...getRtlTextFieldProps(formDirection)}
          />
          <TextField
            select
            margin="dense"
            label={t("stock.unit")}
            fullWidth
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            disabled={unitLoading || !selectedShop}
            SelectProps={selectProps}
            {...getRtlTextFieldProps(formDirection)}
          >
            {branchUnits.map((item) => (
              <MenuItem key={item.id} value={String(item.id)}>
                {item.title}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            margin="dense"
            label={t("stock.currency")}
            fullWidth
            required
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            disabled={currencyLoading || !selectedShop}
            SelectProps={selectProps}
            {...getRtlTextFieldProps(formDirection)}
          >
            {branchCurrencies.map((item) => (
              <MenuItem key={item.id} value={String(item.id)}>
                {item.title}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("stock.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            loading ||
            !selectedShop ||
            !currency
          }
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("stock.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StockEdit;
